import React, { ChangeEvent, FC, FormEvent, useState } from 'react';
import { useMyWholesaleDeals, useWholesalerProfile } from '../../../hooks/api/Wholesale/useWholesale';
import { getErrorMessages } from '../../../utility/team';
import {
  buildDealFormData, COMPANY_RELATIONSHIPS, DEAL_TYPES, DealFormValues, emptyDealForm, MAX_PHOTOS, MAX_VIDEOS,
  mediaProblems, PROPERTY_CLASSES, WholesaleDeal, WholesalerProfile,
} from '../../../utility/wholesale';

const inputClass = 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';
const primaryButton = 'rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-gray-400';
const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';

const Field: FC<{ id: string; label: string; children: React.ReactNode; wide?: boolean }> = ({ id, label, children, wide }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <label htmlFor={id} className="block text-sm font-medium text-gray-900">{label}</label>
    {children}
  </div>
);

const ErrorList: FC<{ error: unknown }> = ({ error }) =>
  error ? <ul className="text-sm text-red-600">{getErrorMessages(error).map((m) => <li key={m}>{m}</li>)}</ul> : null;

// ---- Wholesaler profile ----
const PROFILE_TEXT_FIELDS: [ keyof WholesalerProfile, string ][] = [
  [ 'full_name', 'Your name' ], [ 'phone', 'Phone' ], [ 'email', 'Email' ], [ 'mailing_street', 'Street' ],
  [ 'mailing_city', 'City' ], [ 'mailing_state', 'State (2 letters)' ], [ 'mailing_zip', 'Zip' ],
];

export const WholesalerProfileForm: FC = () => {
  const { profile, save, isLoading } = useWholesalerProfile();
  const [ draft, setDraft ] = useState<Partial<WholesalerProfile>>({});
  const values = { ...profile, ...draft } as WholesalerProfile;
  const set = (key: keyof WholesalerProfile, value: string | boolean) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync(draft);
      setDraft({});
    } catch {
      // shown below from the mutation state
    }
  };

  if (isLoading || !profile) return <p className="mt-4 text-sm text-gray-500">Loading...</p>;

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-6" data-testid="wholesaler-profile-form">
      <div className="grid gap-4 sm:max-w-3xl sm:grid-cols-2">
        {PROFILE_TEXT_FIELDS.map(([ key, label ]) => (
          <Field key={key} id={`wp_${key}`} label={label}>
            <input id={`wp_${key}`} className={inputClass} value={String(values[key] ?? '')} onChange={(e) => set(key, e.target.value)} />
          </Field>
        ))}
        <div className="flex items-center gap-x-2 sm:col-span-2">
          <input id="wp_acts_for_company" type="checkbox" checked={Boolean(values.acts_for_company)} onChange={(e) => set('acts_for_company', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
          <label htmlFor="wp_acts_for_company" className="text-sm text-gray-900">I act on behalf of an LLC or company</label>
        </div>
        {values.acts_for_company && (
          <>
            <Field id="wp_company_name" label="Company name">
              <input id="wp_company_name" className={inputClass} value={String(values.company_name ?? '')} onChange={(e) => set('company_name', e.target.value)} />
            </Field>
            <Field id="wp_company_relationship" label="Your relationship to the company">
              <select id="wp_company_relationship" className={inputClass} value={String(values.company_relationship ?? '')} onChange={(e) => set('company_relationship', e.target.value)}>
                <option value="">Choose...</option>
                {COMPANY_RELATIONSHIPS.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </Field>
          </>
        )}
        <Field id="wp_default_deal_type" label="Usual deal type">
          <select id="wp_default_deal_type" className={inputClass} value={String(values.default_deal_type ?? '')} onChange={(e) => set('default_deal_type', e.target.value)}>
            <option value="">No default</option>
            {Object.entries(DEAL_TYPES).map(([ key, label ]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field id="wp_default_notes" label="Notes to include on every deal" wide>
          <textarea id="wp_default_notes" rows={3} className={inputClass} value={String(values.default_notes ?? '')} onChange={(e) => set('default_notes', e.target.value)} />
        </Field>
      </div>
      <ErrorList error={save.error} />
      {profile.complete === false && (
        <p className="text-sm text-yellow-800">Still needed before you can post: {(profile.missing_required_fields ?? []).join(', ')}</p>
      )}
      <button type="submit" className={primaryButton} disabled={save.isPending || Object.keys(draft).length === 0}>Save profile</button>
    </form>
  );
};

// ---- Deal form ----
const DEAL_NUMBER_FIELDS: [ string, string ][] = [
  [ 'sqft', 'Sq ft' ], [ 'bedrooms', 'Beds' ], [ 'bathrooms', 'Baths' ], [ 'acres', 'Acres' ], [ 'year_built', 'Year built' ],
];

export const DealForm: FC<{ deal?: WholesaleDeal; onDone: () => void }> = ({ deal, onDone }) => {
  const { create, update } = useMyWholesaleDeals();
  const { profile } = useWholesalerProfile();
  const [ values, setValues ] = useState<DealFormValues>(() => deal
    ? Object.fromEntries(Object.keys(emptyDealForm()).map((k) => [ k, String((deal as unknown as Record<string, unknown>)[k] ?? '') ]))
    : emptyDealForm(profile));
  const [ photos, setPhotos ] = useState<File[]>([]);
  const [ videos, setVideos ] = useState<File[]>([]);
  const mutation = deal ? update : create;
  const problems = mediaProblems(photos, videos, deal?.photo_urls.length ?? 0, deal?.video_urls.length ?? 0);
  const set = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const body = buildDealFormData(values, photos, videos, Boolean(deal));
    try {
      if (deal) await update.mutateAsync({ id: deal.id, body }); else await create.mutateAsync(body);
      onDone();
    } catch {
      // shown below from the mutation state
    }
  };
  const pick = (setter: (files: File[]) => void) => (e: ChangeEvent<HTMLInputElement>) => setter(Array.from(e.target.files ?? []));

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4 sm:max-w-3xl" data-testid="wholesale-deal-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="wd_street" label="Street address" wide><input id="wd_street" className={inputClass} value={values.street} onChange={(e) => set('street', e.target.value)} /></Field>
        <Field id="wd_city" label="City"><input id="wd_city" className={inputClass} value={values.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field id="wd_state" label="State (2 letters)"><input id="wd_state" className={inputClass} value={values.state} onChange={(e) => set('state', e.target.value)} /></Field>
        <Field id="wd_zipcode" label="Zip"><input id="wd_zipcode" className={inputClass} value={values.zipcode} onChange={(e) => set('zipcode', e.target.value)} /></Field>
        <Field id="wd_property_class" label="Property type">
          <select id="wd_property_class" className={inputClass} value={values.property_class} onChange={(e) => set('property_class', e.target.value)}>
            {PROPERTY_CLASSES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </Field>
        {DEAL_NUMBER_FIELDS.map(([ key, label ]) => (
          <Field key={key} id={`wd_${key}`} label={label}><input id={`wd_${key}`} type="number" className={inputClass} value={values[key]} onChange={(e) => set(key, e.target.value)} /></Field>
        ))}
        <Field id="wd_wholesale_price" label="Wholesale price ($)"><input id="wd_wholesale_price" type="number" className={inputClass} value={values.wholesale_price} onChange={(e) => set('wholesale_price', e.target.value)} /></Field>
        <Field id="wd_expires_on" label="Deal expires on"><input id="wd_expires_on" type="date" className={inputClass} value={values.expires_on} onChange={(e) => set('expires_on', e.target.value)} /></Field>
        <Field id="wd_deal_type" label="Type of deal">
          <select id="wd_deal_type" className={inputClass} value={values.deal_type} onChange={(e) => set('deal_type', e.target.value)}>
            {Object.entries(DEAL_TYPES).map(([ key, label ]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        {values.deal_type === 'other' && (
          <Field id="wd_deal_type_other" label="Describe the deal"><input id="wd_deal_type_other" className={inputClass} value={values.deal_type_other} onChange={(e) => set('deal_type_other', e.target.value)} /></Field>
        )}
        <Field id="wd_notes" label="Notes" wide><textarea id="wd_notes" rows={4} className={inputClass} value={values.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
        <Field id="wd_photos" label={`Photos (up to ${MAX_PHOTOS}, 5 MB each)`}>
          <input id="wd_photos" type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={pick(setPhotos)} className="text-sm" />
        </Field>
        <Field id="wd_videos" label={`Videos (up to ${MAX_VIDEOS}, 100 MB each)`}>
          <input id="wd_videos" type="file" accept="video/mp4,video/quicktime,video/webm" multiple onChange={pick(setVideos)} className="text-sm" />
        </Field>
      </div>
      {problems.length > 0 && <ul className="text-sm text-red-600">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      <ErrorList error={mutation.error} />
      <div className="flex gap-x-3">
        <button type="submit" className={primaryButton} disabled={mutation.isPending || problems.length > 0}>{deal ? 'Save deal' : 'Post deal'}</button>
        <button type="button" className={secondaryButton} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
};

const WholesalePage: FC = () => {
  const { canPost, profile } = useWholesalerProfile();
  const { deals, remove } = useMyWholesaleDeals();
  const [ editing, setEditing ] = useState<WholesaleDeal | 'new' | null>(null);

  return (
    <div className="px-2">
      <h1 className="text-base font-semibold leading-6 text-gray-900">Wholesale</h1>
      <p className="mt-2 text-sm text-gray-700">
        Post properties you want to wholesale. Pro members can add the Wholesale Market List when they start a list sort and see every deal that has not expired.
      </p>

      <h2 className="mt-6 text-sm font-semibold text-gray-900">Wholesaler profile</h2>
      <WholesalerProfileForm />

      <h2 className="mt-8 text-sm font-semibold text-gray-900">My deals</h2>
      {!canPost && <p className="mt-2 text-sm text-yellow-800">Posting deals needs a monthly Pro subscription.</p>}
      {editing ? (
        <DealForm key={editing === 'new' ? 'new' : editing.id} deal={editing === 'new' ? undefined : editing} onDone={() => setEditing(null)} />
      ) : (
        <>
          <ul className="mt-2 divide-y divide-gray-100 sm:max-w-3xl">
            {deals.length === 0 && <li className="py-2 text-sm text-gray-500">No deals yet.</li>}
            {deals.map((deal) => (
              <li key={deal.id} className="flex items-center justify-between gap-x-4 py-3">
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{deal.full_address}</p>
                  <p className="text-gray-500">
                    ${Number(deal.wholesale_price).toLocaleString()} · {deal.deal_type_label} · {deal.live ? `expires ${deal.expires_on}` : 'not showing (expired or withdrawn)'}
                  </p>
                </div>
                <div className="flex gap-x-2">
                  <button type="button" className={secondaryButton} onClick={() => setEditing(deal)}>Edit</button>
                  <button type="button" className={secondaryButton} onClick={() => { if (window.confirm('Delete this deal?')) remove.mutate(deal.id); }}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
          <button type="button" className={`${primaryButton} mt-4`} disabled={!canPost || !profile?.complete} onClick={() => setEditing('new')}>
            Post a deal
          </button>
          {canPost && profile && !profile.complete && <p className="mt-2 text-sm text-gray-500">Finish your profile above to post.</p>}
        </>
      )}
    </div>
  );
};

export default WholesalePage;
